import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faPaperPlane, faClipboardCheck, faCalendarAlt, faHourglassHalf, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { keyUserRoles } from '../../../assets/js/SessionStorageNames';

// Función para verificar si un producto está revisado
const isProductReviewed = (productId) => {
  try {
    const reviewedProducts = JSON.parse(localStorage.getItem('reviewedProducts') || '[]');
    return reviewedProducts.includes(productId);
  } catch (error) {
    console.error('Error al verificar si está revisado:', error);
    return false;
  }
};

const ProductCardWidget = ({ 
  product, 
  selectedTab, 
  selectedProduct, 
  setSelectedProduct, 
  convertBase64ToDataUrl,
  selectedProducts = [],
  onProductSelect,
  onProductDeselect
}) => {
  const [isReviewed, setIsReviewed] = useState(false);

  // Verificar si el usuario es admin
  const userRoles = localStorage.getItem(keyUserRoles)?.split(",").filter(String) || [];
  const isAdmin = userRoles.includes("ADMIN") || userRoles.includes("ADMIN_ENTERPRISE");

  // Verificar si el producto está revisado
  useEffect(() => {
    if (product) {
      const reviewed = isProductReviewed(product.id);
      setIsReviewed(reviewed);
    }
  }, [product]);

  // Escuchar eventos de productos marcados como revisados
  useEffect(() => {
    const handleProductReviewed = (event) => {
      const { productId } = event.detail;
      if (product && product.id === productId) {
        setIsReviewed(true);
      }
    };

    window.addEventListener('productReviewed', handleProductReviewed);
    
    return () => {
      window.removeEventListener('productReviewed', handleProductReviewed);
    };
  }, [product]);

  // Verificar si este producto está seleccionado
  const isSelected = selectedProducts.some(p => p.id === product.id);

  // Función para manejar la selección del checkbox
  const handleCheckboxChange = (e) => {
    e.stopPropagation(); // Evitar que se abra el modal
    if (e.target.checked) {
      onProductSelect && onProductSelect(product);
    } else {
      onProductDeselect && onProductDeselect(product);
    }
  };

  // Determinar color y badge según estado
  let borderColor = '#1da1f2';
  let badgeBg = '#eaf4fb';
  let badgeColor = '#1da1f2';
  let buttonBg = '#1da1f2';
  let buttonColor = '#fff';
  let badgeIcon = faHourglassHalf;
  let badgeText = 'Pendiente';
  if (selectedTab === 'APPROVED') {
    borderColor = '#27ae60';
    badgeBg = '#eafaf1';
    badgeColor = '#27ae60';
    buttonBg = '#27ae60';
    badgeIcon = faCheckCircle;
    badgeText = 'Aprobado';
  } else if (selectedTab === 'REJECTED') {
    borderColor = '#ff0000';
    badgeBg = '#ffeaea';
    badgeColor = '#ff0000';
    buttonBg = '#ff0000';
    badgeIcon = faTimesCircle;
    badgeText = 'Rechazado';
  } else if (selectedTab === 'PROCESSING') {
    borderColor = '#1da1f2';
    badgeBg = '#eaf4fb';
    badgeColor = '#1da1f2';
    buttonBg = '#1da1f2';
    badgeIcon = faClipboardCheck;
    badgeText = 'En proceso';
  } else if (selectedTab === 'CANCELLED') {
    borderColor = '#e74c3c';
    badgeBg = '#ffeaea';
    badgeColor = '#e74c3c';
    buttonBg = '#e74c3c';
    badgeIcon = faTimesCircle;
    badgeText = 'Cancelado';
  }
  return (
    <div
      key={product.id}
      style={{
        background: '#fff',
        borderRadius: 18,
        boxShadow: '0 4px 24px #0001',
        padding: "18px 30px",
        minWidth: 320,
        width: '95%',
        maxWidth: 340,
        flex: '1 1 320px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderLeft: `8px solid ${borderColor}`,
        border: selectedProduct && selectedProduct.id === product.id ? '2.5px solid #1da1f2' : '2.5px solid #eee',
        transition: 'box-shadow 0.2s, border 0.2s, transform 0.18s',
        marginBottom: 8,
        animation: 'fadeInCard 0.3s',
        position: 'relative',
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 8px 32px #0002'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 4px 24px #0001'}
    >
      {/* Checkbox para selección múltiple - Solo visible para admin, productos revisados y estados PENDING/FAILED */}
      {isAdmin && isReviewed && (selectedTab === 'PENDING' || selectedTab === 'FAILED') && (
        <div style={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 10
        }}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            style={{
              width: 20,
              height: 20,
              cursor: 'pointer',
              accentColor: '#1da1f2'
            }}
          />
        </div>
      )}
      {/* Imagen solo para estados distintos de aprobado/rechazado */}
      {!(selectedTab === 'APPROVED' || selectedTab === 'REJECTED' || selectedTab === 'CANCELLED') && product.mainImage && (
        <img src={convertBase64ToDataUrl(product.mainImage)} alt={product.productName} style={{ width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 10, border: '1.5px solid #eee', marginBottom: 16 }} />
      )}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4, color: '#222' }}>{product.productName}</div>
        {product.ownerName && (
          <div style={{ color: '#222', fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{product.ownerName}</div>
        )}
        <div style={{ color: '#1da1f2', fontWeight: 500, fontSize: 15, marginBottom: 2 }}>{product.enterprise?.name}</div>
        <div style={{ color: '#888', fontSize: 14, marginBottom: 2 }}>{product.brand} &bull; {product.categoryName}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ background: badgeBg, color: badgeColor, fontWeight: 700, fontSize: 15, borderRadius: 16, padding: '4px 16px', display: 'flex', alignItems: 'center', gap: 6 }}>
          <FontAwesomeIcon icon={badgeIcon} /> {badgeText}
        </span>
        
        {/* Indicador de revisado - Solo visible para administradores en estados PENDING/FAILED */}
        {isAdmin && (selectedTab === 'PENDING' || selectedTab === 'FAILED') && (
          isReviewed ? (
            <span style={{ 
              background: 'linear-gradient(135deg, #e8f5e8 0%, #d4edda 100%)', 
              color: '#155724', 
              fontWeight: 700, 
              fontSize: 11, 
              borderRadius: 14, 
              padding: '4px 12px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: 4,
              boxShadow: '0 1px 4px rgba(21, 87, 36, 0.2)',
              border: '1px solid #c3e6cb'
            }}>
              <FontAwesomeIcon icon={faEye} style={{ fontSize: 10 }} /> Revisado
            </span>
          ) : (
            <span style={{ 
              background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)', 
              color: '#856404', 
              fontWeight: 700, 
              fontSize: 11, 
              borderRadius: 14, 
              padding: '4px 12px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: 4,
              boxShadow: '0 1px 4px rgba(133, 100, 4, 0.2)',
              border: '1px solid #ffeaa7'
            }}>
              <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: 10 }} /> No revisado
            </span>
          )
        )}
      </div>
      {/* Motivo de rechazo si existe */}
      {selectedTab === 'REJECTED' && product.reviewerComments && (
        <div style={{ color: '#ff0000', fontWeight: 500, fontSize: 15, marginTop: 10, whiteSpace: 'pre-line', background: '#fff0f0', borderRadius: 8, padding: '8px 12px' }}>
          <b>Motivo:</b> {product.reviewerComments}
        </div>
      )}
      <div style={{ marginTop: 18, color: '#888', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
        {product.submittedAt && (
          <span><FontAwesomeIcon icon={faCalendarAlt} /> {new Date(product.submittedAt).toLocaleDateString('es-ES')}</span>
        )}
      </div>
      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}>
        <button
          style={{
            width: '100%',
            maxWidth: 200,
            background: buttonBg,
            border: 'none',
            color: buttonColor,
            borderRadius: 10,
            padding: '10px 0',
            fontWeight: 700,
            cursor: 'pointer',
            fontSize: 15,
            boxShadow: '0 2px 8px #0001',
            transition: 'background 0.18s',
          }}
          onClick={() => setSelectedProduct(product)}
        >
          Ver detalles
        </button>
      </div>
    </div>
  );
};

export default ProductCardWidget; 